import React, { useState, useEffect, useCallback } from 'react';
import { Routes } from './routes';
import { useDispatch } from "react-redux";
import * as actions from './store/actions/actions';
import UnifiedAPIService from './services/UnifiedAPIService';
import { IntlProvider } from 'react-intl';
import GenericSnackNotification, { SnackNotificationContext, SnackNotification, ISnackNotificationContext } from './legacy-components/notifications/GenericSnackNotification';
import { CloudConsoleClient } from './libs/utils/http/client/HttpClient';
import { ConfirmProvider } from 'material-ui-confirm';
import AuthService from './services/authService';
import TagManager from 'react-gtm-module';
import ConsumerClient, { ConsumerClient2 } from './pages/home/consumer/service/ConsumerClient';
import { Box, Link, Snackbar } from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import { useDialogProps } from 'legacy-components/dialog/Dialog';
import type { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';

interface LocaleConfig {
  locale: string;
  source: string;
  intl: {
    localeData: "en"
  }
}

const localeConfigs: LocaleConfig[] = [{
  locale: 'en',
  source: 'en',
  intl: {
    localeData: "en"
  }
}];

async function loadLocaleData(config: LocaleConfig) {
  return import(`../compiled-lang/${config.source}.json`);
}

const initializeAnalytics = () => {
  if (process.env.REACT_APP_GOOGLE_TAG_MANAGER_ID) {
    TagManager.initialize({
      gtmId: process.env.REACT_APP_GOOGLE_TAG_MANAGER_ID
    });
  }
};

const AppRoutes = () => {
  const dispatch = useDispatch();
  const { open, toggle } = useDialogProps();
  const [errorCode, setErrorCode] = useState<number | undefined>(undefined);
  const [isRequestBlocked, setIsRequestBlocked] = useState(false);

  // Setup API interceptors
  useEffect(() => {
    const redirectResponse = (response: AxiosResponse) => response;

    const interceptError = (error: AxiosError) => {
      setErrorCode(error.response?.status);
      if (error.response?.status === 401 || error.response?.status === 0) {
        setIsRequestBlocked(true);
        // dispatch({ type: actions.clearUserDetails });
        //localStorage.clear();
        //sessionStorage.clear();
         AuthService.login();
      } 
      else if (error.response && (error.response.status === 403 || (error.response.status > 499 && error.response.status < 600))) {
        toggle();
      }
      return Promise.reject(error);
    };

    const requestInterceptor = (config: AxiosRequestConfig) => {
      const token = config.headers?.Authorization;
      
      if (token && isRequestBlocked) {
        setIsRequestBlocked(false);
      }
      
      if (isRequestBlocked) {
        AuthService.login();
        return Promise.reject({ message: 'Requests blocked due to 401', __BLOCKED__: true });
      }
      
      return config;
    };

    // Setup interceptors for all API services
    const services = [UnifiedAPIService, CloudConsoleClient, ConsumerClient, ConsumerClient2];
    
    const interceptorIds = services.map(service => ({
      request: service.interceptors.request.use(requestInterceptor),
      response: service.interceptors.response.use(redirectResponse, interceptError)
    }));

    // Cleanup interceptors on unmount
    return () => {
      services.forEach((service, index) => {
        service.interceptors.request.eject(interceptorIds[index].request);
        service.interceptors.response.eject(interceptorIds[index].response);
      });
    };
  }, [dispatch, toggle]);

  return (
    <>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        style={{ marginTop: '36px' }}
        onClose={toggle}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <>
          {errorCode === 403 &&
            <Alert severity="error" onClose={toggle}>
              <AlertTitle>Insufficient permission.</AlertTitle>
              You don't have access, refer to the <Link target="_blank" href={process.env.REACT_APP_ROLE_DOCS_URL}>console permissions</Link>.
            </Alert>
          }
          {errorCode && errorCode > 499 &&
            <Alert severity="error" onClose={toggle}>
              Oops! Something went wrong. Please try again in a few moments.
            </Alert>
          }
        </>
      </Snackbar>
      <Routes />
    </>
  );
};

const App = () => {
  const [notificationContext] = useState<ISnackNotificationContext>({
    show: value => setSnackNotification(value)
  });

  const [snackNotification, setSnackNotification] = useState<SnackNotification | undefined>();

  useEffect(() => {
    initializeAnalytics();
  }, []);

  return (
    <SnackNotificationContext.Provider value={notificationContext}>
      <ConfirmProvider>
        <AppRoutes />
      </ConfirmProvider>
      <GenericSnackNotification notification={snackNotification} />
    </SnackNotificationContext.Provider>
  );
};

export async function bootstrapApplication(options: { locale: string }) {
  const config = localeConfigs.find(lang => lang.locale === options.locale) ?? localeConfigs[0];
  const messageModule = await loadLocaleData(config);

  return (
    <IntlProvider defaultLocale={'en'} locale={config.locale} messages={messageModule.default}>
      <App />
    </IntlProvider>
  );
}
